import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { subscriberChannel } from '@/lib/realtime'
import { useAuthStore } from '@/stores/auth'
import { dedupe, getCached, invalidateCache, setCached } from '@/lib/query-cache'
import type { Database } from '@/lib/types/database'
import { useMyOrganization } from './useMyOrganization'

type Event = Database['public']['Tables']['events']['Row']
type RpcDashboardEvent =
  Database['public']['Functions']['get_my_dashboard_events']['Returns'][number]

export interface EventWithCounts extends Event {
  /** Deduplicated attendee count (single source of truth: get_event_attendee_count). */
  attendee_count?: number
}

/** Exact least-data JSON projection returned by get_my_participant_event_feed(). */
export type ParticipantEventProjection = Pick<
  Event,
  | 'id'
  | 'created_at'
  | 'updated_at'
  | 'title'
  | 'description'
  | 'location'
  | 'start_date'
  | 'end_date'
  | 'capacity'
  | 'is_public'
  | 'cover_image_url'
  | 'cover_image_focus_x'
  | 'cover_image_focus_y'
  | 'status'
  | 'placeholder_color_theme'
  | 'event_type'
  | 'is_offer_mode'
  | 'event_mode'
  | 'organizer_name'
  | 'timezone'
  | 'language'
  | 'logo_url'
  | 'logo_backdrop'
  | 'cover_video_url'
>

export interface ParticipantFeedEvent extends ParticipantEventProjection {
  /** True only for the caller's own RSVP or e-mail invitation. */
  is_participating: boolean
  /**
   * The caller's own answer, when there is one. `is_participating` is true for an
   * invitation with no answer and for a «no» alike, so on its own it cannot say
   * «Du kommer» truthfully (pass-1 re-score, 04.10.2026: «Du er med» on an event
   * where she had not chosen a date). Read from her own `rsvps` rows.
   */
  rsvp_status?: 'yes' | 'no' | 'maybe' | null
}

/** Fields shared by public/participant cards and full management rows. */
export type EventSurfaceEvent = ParticipantEventProjection & {
  is_participating?: boolean
  rsvp_status?: ParticipantFeedEvent['rsvp_status']
  attendee_count?: number
}

const EMPTY_EVENTS: Event[] = []
const EMPTY_PARTICIPANT_EVENTS: ParticipantFeedEvent[] = []

const WORKSPACE_EVENTS_TTL_MS = 60_000
const WORKSPACE_EVENTS_KEY = 'workspace-events:'

interface WorkspaceSnapshot {
  key: string
  rows: Event[]
}

/** Invalidate every active-organization variant after an event write. */
export function invalidateWorkspaceEvents(): void {
  invalidateCache(WORKSPACE_EVENTS_KEY)
}

const PARTICIPANT_EVENTS_TTL_MS = 60_000
const PARTICIPANT_EVENTS_KEY = 'participant-events:'

interface ParticipantSnapshot {
  key: string
  rows: ParticipantFeedEvent[]
}

export function invalidateParticipantEvents(): void {
  invalidateCache(PARTICIPANT_EVENTS_KEY)
}

const EVENT_QUOTA_TTL_MS = 60_000
const EVENT_QUOTA_KEY = 'event-quota-state:'

export interface EventQuotaState {
  hasCreatedEvent: boolean
  activeEventCount: number
}

const UNKNOWN_QUOTA_STATE: EventQuotaState = {
  hasCreatedEvent: true,
  activeEventCount: 1,
}

export function invalidateEventQuotaState(): void {
  invalidateCache(EVENT_QUOTA_KEY)
}

/**
 * Public discovery plus the caller's own RSVP/invitation rows.
 *
 * Unlike the legacy broad visible-event RPC, this source has no private
 * owner/event-role arm. Mixed organiser pages may safely compose it with the
 * active-workspace source without an off-tenant private name crossing into
 * the browser first.
 *
 * Scoped to the active workspace on the server: her own RSVP on an event of
 * another organisation she belongs to shows in that organisation, not here
 * (YOLObukk in PROFF Event, 19.10.2026). Keyed on the org for the same reason.
 */
export function useParticipantEventFeed() {
  const [snapshot, setSnapshot] = useState<ParticipantSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const userId = useAuthStore((s) => s.user?.id)
  const { activeOrgId, loading: orgLoading } = useMyOrganization()
  const contextKey = userId && !orgLoading
    ? `${PARTICIPANT_EVENTS_KEY}${userId}:${activeOrgId ?? 'personal'}`
    : null

  const fetch = useCallback(async (force = false) => {
    if (!userId || orgLoading || !contextKey) {
      if (!userId) {
        setSnapshot(null)
        setError(null)
        setLoading(false)
      }
      return
    }

    if (!force) {
      const cached = getCached<ParticipantFeedEvent[]>(contextKey, PARTICIPANT_EVENTS_TTL_MS)
      if (cached) {
        setSnapshot({ key: contextKey, rows: cached })
        setError(null)
        setLoading(false)
        return
      }
    }

    setLoading(true)
    try {
      const { data, error: err } = await dedupe(contextKey, async () =>
        await supabase.rpc('get_my_workspace_participant_event_feed')
      )
      if (err) {
        setSnapshot({ key: contextKey, rows: [] })
        setError(err.message)
      } else {
        const participatingIds = (data ?? [])
          .filter((row) => row.is_participating)
          .map((row) => (row.event_data as { id: string }).id)
        const statusByEvent = new Map<string, ParticipantFeedEvent['rsvp_status']>()
        if (participatingIds.length > 0) {
          const { data: own } = await supabase
            .from('rsvps')
            .select('event_id, status')
            .eq('user_id', userId)
            .in('event_id', participatingIds)
          for (const r of own ?? []) statusByEvent.set(r.event_id, r.status as ParticipantFeedEvent['rsvp_status'])
        }
        const rows = (data ?? []).map((row) => {
          const event = row.event_data as ParticipantEventProjection
          return {
            ...event,
            is_participating: row.is_participating,
            rsvp_status: statusByEvent.get(event.id) ?? null,
          }
        })
        setCached(contextKey, rows)
        setSnapshot({ key: contextKey, rows })
        setError(null)
      }
    } catch (err) {
      setSnapshot({ key: contextKey, rows: [] })
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [contextKey, orgLoading, userId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useEffect(() => {
    if (!userId || orgLoading || !contextKey) return
    const channel = subscriberChannel('participant-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => {
          invalidateCache(contextKey)
          void fetch(true)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [contextKey, fetch, orgLoading, userId])

  return {
    events: snapshot?.key === contextKey ? snapshot.rows : EMPTY_PARTICIPANT_EVENTS,
    loading: orgLoading || loading,
    error,
    refetch: () => fetch(true),
  }
}

/**
 * The sole organizer-management event source.
 *
 * Public, invited and RSVP rows deliberately remain in the participant feeds
 * below. This argument-free RPC is the narrower capability: auth.uid() plus
 * active organization, membership and can_edit_event(), enforced on the
 * server before a name reaches the browser.
 */
export function useWorkspaceEvents() {
  const [snapshot, setSnapshot] = useState<WorkspaceSnapshot | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const userId = useAuthStore((s) => s.user?.id)
  const { activeOrgId, loading: orgLoading } = useMyOrganization()
  const contextKey = userId
    ? `${WORKSPACE_EVENTS_KEY}${userId}:${activeOrgId ?? 'personal'}`
    : null

  const fetch = useCallback(async (force = false) => {
    if (!userId || orgLoading || !contextKey) {
      if (!userId) {
        setSnapshot(null)
        setError(null)
        setLoading(false)
      }
      return
    }

    if (!force) {
      const cached = getCached<Event[]>(contextKey, WORKSPACE_EVENTS_TTL_MS)
      if (cached) {
        setSnapshot({ key: contextKey, rows: cached })
        setError(null)
        setLoading(false)
        return
      }
    }

    setLoading(true)
    try {
      const { data, error: err } = await dedupe(contextKey, async () =>
        await supabase.rpc('get_my_workspace_events')
      )
      if (err) {
        // Fail closed on a permission or active-context change. The returned
        // rows are also keyed below, so a previous tenant cannot paint during
        // the render before this state update lands.
        setSnapshot({ key: contextKey, rows: [] })
        setError(err.message)
      } else {
        const rows = (data ?? []) as Event[]
        setCached(contextKey, rows)
        setSnapshot({ key: contextKey, rows })
        setError(null)
      }
    } catch (err) {
      setSnapshot({ key: contextKey, rows: [] })
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [contextKey, orgLoading, userId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  useEffect(() => {
    if (!userId || orgLoading || !contextKey) return
    const channel = subscriberChannel('workspace-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => {
          invalidateCache(contextKey)
          void fetch(true)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [contextKey, fetch, orgLoading, userId])

  const events = snapshot?.key === contextKey ? snapshot.rows : EMPTY_EVENTS
  return {
    events,
    loading: orgLoading || loading,
    error,
    refetch: () => fetch(true),
  }
}

/** Exact client readback of the population enforced by trg_enforce_event_quota. */
export function useEventQuotaState() {
  const [snapshot, setSnapshot] = useState<{
    key: string
    value: EventQuotaState
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const userId = useAuthStore((s) => s.user?.id)
  const contextKey = userId ? `${EVENT_QUOTA_KEY}${userId}` : null

  const fetch = useCallback(async (force = false) => {
    if (!userId || !contextKey) {
      setSnapshot(null)
      setError(null)
      setLoading(false)
      return
    }
    if (!force) {
      const cached = getCached<EventQuotaState>(contextKey, EVENT_QUOTA_TTL_MS)
      if (cached) {
        setSnapshot({ key: contextKey, value: cached })
        setError(null)
        setLoading(false)
        return
      }
    }
    setLoading(true)
    try {
      const { data, error: err } = await dedupe(contextKey, async () =>
        await supabase.rpc('get_my_event_quota_state')
      )
      if (err) {
        setSnapshot({ key: contextKey, value: UNKNOWN_QUOTA_STATE })
        setError(err.message)
      } else {
        const row = data?.[0]
        const value = {
          hasCreatedEvent: row?.has_created_event ?? false,
          activeEventCount: row?.active_event_count ?? 0,
        }
        setCached(contextKey, value)
        setSnapshot({ key: contextKey, value })
        setError(null)
      }
    } catch (err) {
      setSnapshot({ key: contextKey, value: UNKNOWN_QUOTA_STATE })
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [contextKey, userId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const value = snapshot?.key === contextKey
    ? snapshot.value
    : UNKNOWN_QUOTA_STATE
  return { ...value, loading, error, refetch: () => fetch(true) }
}

/** Workspace rows enriched only where an organizer surface displays counts. */
export function useWorkspaceEventsWithCounts() {
  const workspace = useWorkspaceEvents()
  const [snapshot, setSnapshot] = useState<{ ids: string; rows: EventWithCounts[] } | null>(null)
  const ids = workspace.events.map((event) => event.id).join(',')

  useEffect(() => {
    if (workspace.loading || workspace.error) return
    if (!ids) return
    let cancelled = false
    Promise.all(
      workspace.events.map(async (event) => {
        const { data: count } = await supabase.rpc('get_event_attendee_count', {
          p_event_id: event.id,
        })
        return {
          ...event,
          attendee_count: (count as unknown as number) ?? 0,
        } as EventWithCounts
      })
    ).then((rows) => {
      if (!cancelled) setSnapshot({ ids, rows })
    })
    return () => {
      cancelled = true
    }
  }, [ids, workspace.error, workspace.events, workspace.loading])

  return {
    ...workspace,
    events: ids && snapshot?.ids === ids ? snapshot.rows : [],
    loading: workspace.loading || (Boolean(ids) && snapshot?.ids !== ids),
  }
}

/** Fetch dashboard events via RPC (owned + admin + invited) */
export function useDashboardEvents() {
  const [events, setEvents] = useState<RpcDashboardEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)

  const fetch = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error: err } = await supabase.rpc('get_my_dashboard_events')
      if (err) {
        setError(err.message)
      } else {
        setEvents(data ?? [])
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetch()
  }, [fetch])

  // Real-time: refetch on event changes
  useEffect(() => {
    if (!user) return
    const channel = subscriberChannel('dashboard-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => fetch()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetch])

  return { events, loading, error, refetch: fetch }
}

/** Fetch all visible events for user via RPC */
export function useVisibleEvents() {
  const [events, setEvents] = useState<EventWithCounts[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)

  const fetch = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error: err } = await supabase.rpc(
        'get_visible_events_for_user',
        { requesting_user_id: user.id }
      )
      if (err) {
        setError(err.message)
      } else {
        // Preserve the legacy hook's existing contract for callers outside
        // the organiser workspace. Mixed organiser pages use the narrower
        // participant feed above and never request these counts for public or
        // off-tenant rows.
        const enriched = await Promise.all(
          (data ?? []).map(async (event) => {
            const { data: count } = await supabase.rpc('get_event_attendee_count', {
              p_event_id: event.id,
            })
            return {
              ...event,
              attendee_count: (count as unknown as number) ?? 0,
            } as EventWithCounts
          })
        )
        setEvents(enriched)
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetch()
  }, [fetch])

  useEffect(() => {
    if (!user) return
    const channel = subscriberChannel('visible-events')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => fetch()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetch])

  return { events, loading, error, refetch: fetch }
}

/**
 * Events the current user may actually target from New Broadcast.
 *
 * This is intentionally NOT a client-side subset of `useVisibleEvents`.
 * Visible events include public, invited and attended events; fetching those
 * names and hiding them afterwards would still cross the tenant boundary in
 * the browser. The argument-free RPC binds itself to auth.uid(), the active
 * tenant and can_edit_event(). Platform administration is not send authority.
 */
export function useBroadcastManageableEvents() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)

  const fetch = useCallback(async () => {
    if (!user) {
      setEvents([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error: err } = await supabase.rpc('get_broadcast_manageable_events')
      if (err) {
        // Fail closed. A manual refetch can follow an active-tenant switch or
        // a permission change; retaining the previous capability list on an
        // error would leave stale event names and selectable ids on screen.
        setEvents([])
        setError(err.message)
      } else {
        setEvents((data ?? []) as Event[])
        setError(null)
      }
    } catch (err) {
      setEvents([])
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { events, loading, error, refetch: fetch }
}

/**
 * The same visible-event set, WITHOUT the per-event attendee count.
 *
 * `useVisibleEvents` enriches every row through `get_event_attendee_count`, one
 * RPC per event. That is correct for a list of cards that each show a number,
 * and pure waste for a caller that only wants to know how many events the user
 * owns — which is what `useSubscription` wants, and `useSubscription` mounts in
 * the sidebar, the bottom nav, the route guards and the upgrade prompt.
 *
 * Measured on «Nasjonalt salgsmøte 2026» before this split: opening the
 * «Kunngjøringer» tab fired 16 distinct requests, ten of them
 * `get_event_attendee_count` — one per event in David's list — because the tab
 * rendered a per-event SMS composer, which asked `useSubscription` a single boolean
 * (`canUseSms`). None of those ten counts was ever read.
 *
 * Cached and de-duplicated: the answer changes when you create or delete an
 * event, not between two mounts in the same minute.
 */
const VISIBLE_EVENTS_TTL_MS = 60_000
const VISIBLE_EVENTS_KEY = 'visible-events-lite:'

/**
 * Call after creating or deleting an event.
 *
 * The set only changes on those two acts, and both of them are followed by a
 * screen that reads the gate again (the limit modal, the events list). The
 * server owns the real quota — `trg_enforce_event_quota` is a BEFORE INSERT
 * trigger on `events` — so a stale client list can never let anyone past it;
 * this only keeps the number the organiser SEES honest in the same second.
 */
export function invalidateVisibleEvents(): void {
  invalidateCache(VISIBLE_EVENTS_KEY)
  invalidateWorkspaceEvents()
  invalidateParticipantEvents()
  invalidateEventQuotaState()
}

export function useVisibleEventsLite() {
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)

  const fetch = useCallback(async () => {
    if (!user) {
      setLoading(false)
      return
    }
    const cacheKey = `${VISIBLE_EVENTS_KEY}${user.id}`
    const cached = getCached<Event[]>(cacheKey, VISIBLE_EVENTS_TTL_MS)
    if (cached) {
      setEvents(cached)
      setError(null)
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error: err } = await dedupe(cacheKey, async () =>
        supabase.rpc('get_visible_events_for_user', { requesting_user_id: user.id })
      )
      if (err) {
        setError(err.message)
      } else {
        const rows = (data ?? []) as Event[]
        setCached(cacheKey, rows)
        setEvents(rows)
        setError(null)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    fetch()
  }, [fetch])

  return { events, loading, error, refetch: fetch }
}
